import {TKey} from '@/translations/i18n.tsx'
import {
    UserExtended,
    ContractType,
    UserQualificationType,
    UserFormSchema,
    CreateUserFormValidationSchema,
    CogeapsCertification,
    Period,
    UsersColumn,
    BaseUserColumns,
    CustomUserColumns,
    UserEventsColumn
} from '@/features/users/types.ts'
import dayjs from 'dayjs'
import {capitalize, ObjectEntries, ObjectKeys} from '@/utilities/helpers'
import {formatDate} from '@/dayjs'
import {FieldNamesMarkedBoolean} from 'react-hook-form'
import {palette} from '@/theme/palette'
import {ConsultantAbsenceReason} from '../agenda/types'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {z} from 'zod'
import {authPermissionsToPermissionKeys} from '../auth/utils'
import {parsePhoneNumber} from 'react-phone-number-input'

// maps
export const userStatusToLabel = {
    operative: 'users:status_operative',
    candidate: 'users:status_candidate',
    suspended: 'users:status_suspended',
    absent: 'users:status_absent',
    terminated: 'users:status_terminated'
} as const satisfies Record<UserExtended['status1'], TKey>

export const userStatusMapToColor = {
    absent: palette.light.orange['500'],
    candidate: palette.light.primary['400'],
    operative: palette.light.green['500'],
    suspended: palette.light.red['500'],
    terminated: palette.light.neutral['400']
} as const satisfies Record<UserExtended['status1'], string>

export const acceptedFileTypes = {
    'application/pdf': ['.pdf']
}

export const contractTypeToLabel = {
    consultant: 'commons:consultant',
    flat_rate: 'users:flat_rate',
    employee: 'users:employee'
} as const satisfies Record<ContractType, TKey>

export const userQualificationToLabel = {
    bachelor_degree: 'commons:qualification_bachelor_degree',
    master_degree: 'commons:qualification_master_degree',
    doctorate: 'commons:qualification_doctorate',
    specialization: 'commons:specialization'
} as const satisfies Record<UserQualificationType, TKey>

export const paymentMethodToLabel = {
    bank_transfer: 'commons:bank_transfer'
} as const satisfies Record<NonNullable<UserExtended['paymentMethod']>, TKey>

export const userContactLabelsMapToLabel = {
    home: 'users:contact_home',
    office: 'users:contact_office',
    consort: 'users:contact_spouse',
    parent: 'users:contact_parent',
    personal: 'commons:personal'
} as const satisfies Record<string, TKey>

export const userInterruptionReasonToLabel = {
    health_reasons: 'users:interruption_health_reasons',
    personal_reasons: 'users:interruption_personal_reasons',
    unavailability: 'users:interruption_unavailability',
    transfer: 'users:interruption_transfer',
    incompatibility: 'users:interruption_incompatibility',
    retirement: 'users:interruption_retirement'
} as const satisfies Record<NonNullable<UserExtended['interruptionReason']>, TKey>

export const cogeapsCertificationToLabel = {
    certifiable: 'users:cogeaps_certification_certifiable',
    excluded: 'users:cogeaps_certification_excluded',
    not_certifiable: 'users:cogeaps_certification_not_certifiable'
} as const satisfies Record<CogeapsCertification, TKey>

export const columnNameToLabel = {
    [UsersColumn.enum.fullName]: 'commons:name_header',
    [UsersColumn.enum.status]: 'commons:status',
    [UsersColumn.enum.specializationId]: 'commons:specialization',
    [UsersColumn.enum.roleId]: 'users:access_profile',
    [UsersColumn.enum.sector]: 'commons:workSector',
    [UsersColumn.enum.lastLoginAt]: 'users:latest_login_header',
    [UsersColumn.enum.createdAt]: 'users:creation_header',
    [UsersColumn.enum.contactEmail]: 'users:contact_email',
    [UsersColumn.enum.birthDate]: 'commons:birth_date_label',
    [UsersColumn.enum.mobilePhone]: 'commons:mobile_phone',
    [UsersColumn.enum.telephone]: 'commons:telephone'
} as const satisfies Record<BaseUserColumns, TKey | null>

/**
 * Labels for the export-only custom user columns — fields that can be exported but are not shown
 * in the data table (so they are absent from {@link columnNameToLabel}).
 */
export const customColumnNameToLabel = {
    [CustomUserColumns.enum.taxCode]: 'users:user_column_tax_code',
    [CustomUserColumns.enum.patientAgeRange]: 'users:user_column_patient_age_range',
    [CustomUserColumns.enum.startDate]: 'users:user_column_start_date',
    [CustomUserColumns.enum.interruptionDate]: 'users:interruption_date',
    [CustomUserColumns.enum.registrationDate]: 'users:user_column_registration_date',
    [CustomUserColumns.enum.fm08Evaluation]: 'users:user_column_fm08_evaluation',
    [CustomUserColumns.enum.fm08EvaluationLastUpdate]: 'users:user_column_fm08_evaluation_last_update',
    [CustomUserColumns.enum.curriculumLastUpdate]: 'users:user_column_curriculum_last_update',
    [CustomUserColumns.enum.fm68]: 'users:user_column_fm68',
    [CustomUserColumns.enum.privacy]: 'users:user_column_privacy',
    [CustomUserColumns.enum.professionalInsuranceLastVerify]: 'users:user_column_professional_insurance_last_verify',
    [CustomUserColumns.enum.workSuitableUpTo]: 'users:user_column_work_suitable_up_to',
    [CustomUserColumns.enum.threeYearsEcm]: 'users:user_column_three_years_ecm',
    [CustomUserColumns.enum.residence]: 'users:user_column_residence',
    [CustomUserColumns.enum.domicile]: 'users:user_column_domicile'
} as const satisfies Record<CustomUserColumns, TKey>

/**
 * Complete column → label map for users exports: the data-table columns plus the export-only
 * custom columns. Single source of truth for the export-columns selector and the export history
 * column summary (so neither falls back to a humanised English guess).
 */
export const exportColumnNameToLabel = {
    ...columnNameToLabel,
    ...customColumnNameToLabel
} as const

/**
 * Column → label map for the consultant visits table and the `user_events` export.
 * Mirrors `patientEventColumnToLabel`, with "Paziente" where the patient table shows "Operatore".
 */
export const userEventColumnToLabel = {
    [UserEventsColumn.enum.date]: 'patients:event_date',
    [UserEventsColumn.enum.time]: 'commons:time',
    [UserEventsColumn.enum.status]: 'commons:status',
    [UserEventsColumn.enum.accessType]: 'agenda:event_type',
    [UserEventsColumn.enum.patientId]: 'commons:patient',
    [UserEventsColumn.enum.sector]: 'accesses:mode',
    [UserEventsColumn.enum.isRemote]: 'accesses:remote',
    [UserEventsColumn.enum.projectCode]: 'commons:pri_n',
    [UserEventsColumn.enum.projectStartDate]: 'projects:start_date',
    [UserEventsColumn.enum.projectExpirationDate]: 'commons:expiration'
} as const satisfies Record<Exclude<UserEventsColumn, 'select'>, TKey | null>

// helpers
export const retrieveAddressDataFromComponents = (addressComponents: google.maps.GeocoderAddressComponent[]) => {
    const address = addressComponents.find(component => component.types.includes('route'))?.short_name ?? ''
    const city =
        addressComponents.find(component => component.types.includes('administrative_area_level_3'))?.short_name ?? ''
    const streetNumber =
        addressComponents.find(component => component.types.includes('street_number'))?.short_name ?? ''
    const province =
        addressComponents.find(component => component.types.includes('administrative_area_level_2'))?.short_name ?? ''
    const provinceCode =
        addressComponents.find(component => component.types.includes('administrative_area_level_2'))?.short_name ?? ''
    const zipCode = addressComponents.find(component => component.types.includes('postal_code'))?.short_name ?? ''

    return {
        address,
        city: {name: city, province: {acronym: province, name: ''}},
        streetNumber,
        province: {acronym: province, name: ''},
        provinceCode,
        zipCode
    }
}

export const generateYearOptions = (startDate: Date, {yearsBefore = 0, yearsAfter = 0}) => {
    const startYear = dayjs(startDate).year()
    const totalYears = yearsBefore + yearsAfter + 1

    return Array.from({length: totalYears}, (_, i) => startYear - yearsBefore + i).reduce(
        (acc, year) => ({
            ...acc,
            [year]: year.toString()
        }),
        {}
    )
}

// remappers
export const getUserDirtyFieldsFromFormValues = (
    dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<UserFormSchema>>>,
    formValues: Partial<UserFormSchema>
): NonNullable<Partial<UserFormSchema>> => {
    if (typeof dirtyFields != 'object' || dirtyFields == null || !formValues) {
        return {}
    }

    const dirtyFieldsKeys = ObjectKeys(dirtyFields)

    return dirtyFieldsKeys.reduce((acc, key) => {
        const isDirty = dirtyFields[key]
        const value = formValues[key]

        return isDirty
            ? {
                  ...acc,
                  [key]: value
              }
            : acc
    }, {})
}

export const remapUserToDefaultValues = (user: UserExtended) => {
    const remappedUser = Object.fromEntries(
        ObjectEntries(user).map(entry => {
            if (!entry) {
                throw new Error('user entry is falsy')
            }

            const [key, value] = entry

            switch (key) {
                case 'absenceOfIncompatibilityLastVerify': {
                    return [
                        'absenceOfIncompatibilityLastVerify',
                        user.absenceOfIncompatibilityLastVerify?.toString() ?? null
                    ]
                }
                case 'birthDate': {
                    return ['birthDate', new Date(user.birthDate)]
                }
                case 'candidatureDate': {
                    return ['candidatureDate', user.candidatureDate ? new Date(user.candidatureDate) : null]
                }
                case 'curriculum': {
                    return ['curriculumAttachment', user.curriculum]
                }
                case 'curriculumLastUpdate': {
                    return [
                        'curriculumLastUpdate',
                        user.curriculumLastUpdate ? new Date(user.curriculumLastUpdate) : null
                    ]
                }
                case 'amount': {
                    return ['amount', user.amount?.toString() ?? null]
                }
                case 'domicile': {
                    return [
                        'domicile',
                        user.domicile
                            ? {
                                  address: user.domicile.address,
                                  city: user.domicile.city,
                                  streetNumber: user.domicile.streetNumber,
                                  postalCode: user.domicile.cap,
                                  province: {acronym: user.domicile.city.provinceAcronym}
                              }
                            : null
                    ]
                }
                case 'domicileAslDistrict': {
                    return [
                        'domicileAslDistrict',
                        user.domicileAslDistrict?.id
                            ? {
                                  id: user.domicileAslDistrict?.id,
                                  name: user.domicileAslDistrict?.name,
                                  asl: {
                                      id: user.domicileAslDistrict?.aslId,
                                      name: user.domicileAslDistrict?.aslName
                                  }
                              }
                            : null
                    ]
                }
                case 'ecmScore': {
                    return [
                        'ecmScore',
                        user.ecmScore
                            ? {
                                  period: {
                                      label: `${dayjs(user.ecmScore.startDate).year()} - ${dayjs(user.ecmScore.endDate).subtract(1, 'year').year()}`,
                                      startDate: dayjs(user.ecmScore.startDate).toDate(),
                                      endDate: dayjs(user.ecmScore.endDate).toDate()
                                  },
                                  score: user.ecmScore.score,
                                  cogeapsCertification: user.ecmScore.cogeapsCertification
                              }
                            : null
                    ]
                }
                case 'fm07Evaluation': {
                    return ['fm07Evaluation', user.fm07Evaluation?.toString() ?? null]
                }
                case 'fm07EvaluationAttachment': {
                    return ['fm07EvaluationAttachment', user.fm07EvaluationAttachment]
                }
                case 'fm08Evaluation': {
                    return ['fm08Evaluation', user.fm08Evaluation?.toString() ?? null]
                }
                case 'fm08EvaluationAttachment': {
                    return ['fm08EvaluationAttachment', user.fm08EvaluationAttachment]
                }
                case 'fm08EvaluationLastUpdate': {
                    return [
                        'fm08EvaluationLastUpdate',
                        user.fm08EvaluationLastUpdate ? new Date(user.fm08EvaluationLastUpdate) : new Date()
                    ]
                }
                case 'interruptionDate': {
                    return ['interruptionDate', user.interruptionDate ? new Date(user.interruptionDate) : null]
                }
                case 'permissions': {
                    return ['permissions', user.permissions ? authPermissionsToPermissionKeys(user.permissions) : []]
                }
                case 'privacyYear': {
                    return ['privacyYear', user.privacyYear?.toString()]
                }
                case 'professionalInsuranceLastVerify': {
                    return [
                        'professionalInsuranceLastVerify',
                        user.professionalInsuranceLastVerify ? new Date(user.professionalInsuranceLastVerify) : null
                    ]
                }
                case 'qualifications': {
                    return [
                        'qualifications',
                        user.qualifications && user.qualifications.length > 0
                            ? user.qualifications?.map(qualification => ({
                                  ...qualification,
                                  graduationYear: qualification.graduationYear
                                      ? qualification.graduationYear.toString()
                                      : undefined
                              }))
                            : null
                    ]
                }
                case 'registrationDate': {
                    return ['registrationDate', user.registrationDate ? new Date(user.registrationDate) : null]
                }
                case 'residence': {
                    return [
                        'residence',
                        user.residence
                            ? {
                                  address: user.residence.address,
                                  city: user.residence.city,
                                  streetNumber: user.residence.streetNumber,
                                  postalCode: user.residence.cap,
                                  province: {acronym: user.residence.city.provinceAcronym}
                              }
                            : null
                    ]
                }
                case 'residenceAslDistrict': {
                    return [
                        'residenceAslDistrict',
                        user.residenceAslDistrict?.id
                            ? {
                                  id: user.residenceAslDistrict?.id,
                                  name: user.residenceAslDistrict?.name,
                                  asl: {
                                      id: user.residenceAslDistrict?.aslId,
                                      name: user.residenceAslDistrict?.aslName
                                  }
                              }
                            : null
                    ]
                }
                case 'startDate': {
                    return ['startDate', user.startDate ? new Date(user.startDate) : null]
                }
                case 'status1': {
                    return ['status', user.status1]
                }
                case 'suspensionEndDate': {
                    return ['suspensionEndDate', user.suspensionEndDate ? new Date(user.suspensionEndDate) : null]
                }
                case 'suspensionStartDate': {
                    return ['suspensionStartDate', user.suspensionStartDate ? new Date(user.suspensionStartDate) : null]
                }
                case 'temporaryAbsenceEndDate': {
                    return [
                        'temporaryAbsenceEndDate',
                        user.temporaryAbsenceEndDate ? new Date(user.temporaryAbsenceEndDate) : null
                    ]
                }
                case 'temporaryAbsenceStartDate': {
                    return [
                        'temporaryAbsenceStartDate',
                        user.temporaryAbsenceStartDate ? new Date(user.temporaryAbsenceStartDate) : null
                    ]
                }
                case 'workAreas': {
                    return ['workAreas', user.workAreas ?? []]
                }
                case 'workSuitableUpTo': {
                    return ['workSuitableUpTo', user.workSuitableUpTo ? new Date(user.workSuitableUpTo) : null]
                }
                case 'phoneNumber': {
                    return ['phoneNumber', user.phoneNumber ? parsePhoneNumber(user.phoneNumber, 'IT')?.number : null]
                }
                case 'contacts': {
                    return ['contacts', user.contacts ?? []]
                }
                default: {
                    return [key, value]
                }
            }
        })
    )

    return {
        ...remappedUser,
        birthProvince: user.birthCity ? {acronym: user.birthCity.provinceAcronym} : null,
        isBornAbroad: user?.birthCountry?.name != 'Italia',
        isDifferentDomicile:
            user?.residence?.id != user?.domicile?.id && user.residence != null && user.domicile != null
    }
}

export const remapUserToPayload = (formValues: Partial<CreateUserFormValidationSchema>, skipNullish?: boolean) => {
    return Object.fromEntries(
        ObjectEntries(formValues)
            .map(entry => {
                if (!entry) {
                    throw new Error('user entry is falsy')
                }

                const [key, value] = entry

                switch (key) {
                    case 'lastName': {
                        return ['lastName', value ? capitalize(value) : null]
                    }

                    case 'firstName': {
                        return ['firstName', value ? capitalize(value) : null]
                    }

                    case 'absenceOfIncompatibilityLastVerify': {
                        return ['absenceOfIncompatibilityLastVerify', value ? parseInt(value) : null]
                    }
                    case 'birthCity': {
                        return ['birthCityId', value?.id ?? null]
                    }
                    case 'amount': {
                        return ['amount', value ? Number(value) : null]
                    }
                    case 'birthCountry': {
                        return ['birthCountryId', value?.id]
                    }
                    case 'birthDate': {
                        return ['birthDate', value && formatDate(value.toISOString(), 'YYYY-MM-DD')]
                    }
                    case 'candidatureDate': {
                        return ['candidatureDate', value ? formatDate(value.toISOString(), 'YYYY-MM-DD') : null]
                    }
                    case 'pec': {
                        return ['pec', value ?? null]
                    }
                    case 'curriculumAttachment': {
                        return ['curriculumAttachmentId', value?.id ?? null]
                    }
                    case 'curriculumLastUpdate': {
                        return ['curriculumLastUpdate', value ? formatDate(value.toISOString(), 'YYYY-MM-DD') : null]
                    }
                    case 'domicile': {
                        return [
                            'domicile',
                            value?.address
                                ? {
                                      address: value?.address,
                                      city: value?.city?.name,
                                      postalCode: value?.postalCode,
                                      streetNumber: value?.streetNumber || 'snc'
                                  }
                                : null
                        ]
                    }
                    case 'domicileAslDistrict': {
                        return ['domicileAslDistrictId', value?.id ?? null]
                    }
                    case 'ecmScore': {
                        return [
                            'ecmScore',
                            value?.score && value?.period
                                ? {
                                      startDate: formatDate(value.period.startDate, 'YYYY-MM-DD'),
                                      endDate: formatDate(value.period.endDate, 'YYYY-MM-DD'),
                                      score: value.score,
                                      cogeapsCertification: value.cogeapsCertification
                                  }
                                : null
                        ]
                    }
                    case 'fm07Evaluation': {
                        return ['fm07Evaluation', value ? parseInt(value) : null]
                    }
                    case 'fm07EvaluationAttachment': {
                        return ['fm07EvaluationAttachmentId', value?.id ?? null]
                    }
                    case 'fm08Evaluation': {
                        return ['fm08Evaluation', value ? parseInt(value) : null]
                    }
                    case 'fm08EvaluationAttachment': {
                        return ['fm08EvaluationAttachmentId', value?.id ?? null]
                    }
                    case 'fm08EvaluationLastUpdate': {
                        return ['fm08EvaluationLastUpdate', value ? formatDate(value, 'YYYY-MM-DD') : new Date()]
                    }
                    case 'interruptionDate': {
                        return ['interruptionDate', value ? formatDate(value.toISOString(), 'YYYY-MM-DD') : null]
                    }
                    case 'permissions': {
                        return ['permissionNames', value ?? []]
                    }
                    case 'privacyYear': {
                        return ['privacyYear', value ? parseInt(value) : null]
                    }
                    case 'professionalInsuranceLastVerify': {
                        return [
                            'professionalInsuranceLastVerify',
                            value ? formatDate(value.toISOString(), 'YYYY-MM-DD') : null
                        ]
                    }
                    case 'qualifications': {
                        return [
                            'qualifications',
                            value
                                ? value.map(q => ({
                                      ...q,
                                      graduationYear: q.graduationYear && parseInt(q.graduationYear)
                                  }))
                                : []
                        ]
                    }
                    case 'raExempt': {
                        return ['raExempt', !!value]
                    }
                    case 'registrationDate': {
                        return ['registrationDate', value ? formatDate(value.toISOString(), 'YYYY-MM-DD') : null]
                    }
                    case 'residence': {
                        return [
                            'residence',
                            value?.address
                                ? {
                                      address: value?.address,
                                      city: value?.city?.name,
                                      postalCode: value?.postalCode,
                                      streetNumber: value?.streetNumber || 'snc'
                                  }
                                : null
                        ]
                    }
                    case 'residenceAslDistrict': {
                        return ['residenceAslDistrictId', value?.id ?? null]
                    }
                    case 'role': {
                        return ['roleId', value?.id ?? null]
                    }
                    case 'specialization': {
                        return ['specializationId', value?.id]
                    }
                    case 'startDate': {
                        return ['startDate', value ? formatDate(value.toISOString(), 'YYYY-MM-DD') : null]
                    }
                    case 'suspensionEndDate': {
                        return ['suspensionEndDate', value ? formatDate(value.toISOString(), 'YYYY-MM-DD') : null]
                    }
                    case 'suspensionStartDate': {
                        return ['suspensionStartDate', value ? formatDate(value.toISOString(), 'YYYY-MM-DD') : null]
                    }
                    case 'temporaryAbsenceEndDate': {
                        return ['temporaryAbsenceEndDate', value ? formatDate(value.toISOString(), 'YYYY-MM-DD') : null]
                    }
                    case 'temporaryAbsenceStartDate': {
                        return [
                            'temporaryAbsenceStartDate',
                            value ? formatDate(value.toISOString(), 'YYYY-MM-DD') : null
                        ]
                    }
                    case 'weeklyHours': {
                        return ['weeklyHours', value ?? null]
                    }
                    case 'workAreas': {
                        return ['workAreas', value ? value.map(workArea => workArea.id) : []]
                    }
                    case 'workSuitableUpTo': {
                        return ['workSuitableUpTo', value ? formatDate(value.toISOString(), 'YYYY-MM') : null]
                    }
                    case 'notificationsRecipients': {
                        return ['notificationRecipientIds', value ? value.map(recipient => recipient.id) : []]
                    }
                    case 'contacts': {
                        return [
                            'contacts',
                            value && value.length > 0
                                ? value.map(contact => ({
                                      id: contact.id,
                                      label: contact.label,
                                      type: contact.type,
                                      contact: contact.contact
                                  }))
                                : []
                        ]
                    }
                    default: {
                        return [key, value]
                    }
                }
            })
            .filter(entry => {
                const [, value] = entry
                const isEmptyArray = Array.isArray(value) && value.length == 0
                const isEmptyObject = typeof value == 'object' && value != null && ObjectKeys(value).length == 0
                const shouldKeepValue = !!(value || typeof value == 'boolean')
                return !skipNullish || (shouldKeepValue && !isEmptyArray && !isEmptyObject)
            })
    )
}

export const remapUserToJobDataDefaultValues = (user: UserExtended) => {
    const remappedUser = Object.fromEntries(
        ObjectEntries(user).map(entry => {
            if (!entry) {
                throw new Error('user entry is falsy')
            }

            const [key, value] = entry

            switch (key) {
                case 'absenceOfIncompatibilityLastVerify': {
                    return [
                        'absenceOfIncompatibilityLastVerify',
                        user.absenceOfIncompatibilityLastVerify?.toString() ?? null
                    ]
                }
                case 'candidatureDate': {
                    return ['candidatureDate', user.candidatureDate ? new Date(user.candidatureDate) : null]
                }
                case 'curriculum': {
                    return ['curriculumAttachment', user.curriculum]
                }
                case 'curriculumLastUpdate': {
                    return [
                        'curriculumLastUpdate',
                        user.curriculumLastUpdate ? new Date(user.curriculumLastUpdate) : null
                    ]
                }
                case 'amount': {
                    return ['amount', user.amount?.toString() ?? null]
                }
                case 'ecmScore': {
                    return [
                        'ecmScore',
                        user.ecmScore
                            ? {
                                  period: {
                                      name: `${dayjs(user.ecmScore?.startDate).year()} - ${dayjs(user.ecmScore?.endDate).year()}`,
                                      startDate: dayjs(user.ecmScore?.startDate).toDate(),
                                      endDate: dayjs(user.ecmScore?.endDate).toDate()
                                  },
                                  score: user.ecmScore?.score,
                                  cogeapsCertification: user.ecmScore?.cogeapsCertification
                              }
                            : null
                    ]
                }
                case 'fm07Evaluation': {
                    return ['fm07Evaluation', user.fm07Evaluation?.toString() ?? null]
                }
                case 'fm07EvaluationAttachment': {
                    return ['fm07EvaluationAttachment', user.fm07EvaluationAttachment]
                }
                case 'fm08Evaluation': {
                    return ['fm08Evaluation', user.fm08Evaluation?.toString() ?? null]
                }
                case 'fm08EvaluationAttachment': {
                    return ['fm08EvaluationAttachment', user.fm08EvaluationAttachment]
                }
                case 'fm08EvaluationLastUpdate': {
                    return [
                        'fm08EvaluationLastUpdate',
                        user.fm08EvaluationLastUpdate ? new Date(user.fm08EvaluationLastUpdate) : null
                    ]
                }
                case 'interruptionDate': {
                    return ['interruptionDate', user.interruptionDate ? new Date(user.interruptionDate) : null]
                }
                case 'permissions': {
                    return ['permissions', user.permissions ? authPermissionsToPermissionKeys(user.permissions) : []]
                }
                case 'privacyYear': {
                    return ['privacyYear', user.privacyYear?.toString()]
                }
                case 'professionalInsuranceLastVerify': {
                    return [
                        'professionalInsuranceLastVerify',
                        user.professionalInsuranceLastVerify ? new Date(user.professionalInsuranceLastVerify) : null
                    ]
                }
                case 'qualifications': {
                    return [
                        'qualifications',
                        user.qualifications && user.qualifications.length > 0
                            ? user.qualifications?.map(qualification => ({
                                  ...qualification,
                                  graduationYear: qualification.graduationYear
                                      ? qualification.graduationYear.toString()
                                      : undefined
                              }))
                            : null
                    ]
                }
                case 'registrationDate': {
                    return ['registrationDate', user.registrationDate ? new Date(user.registrationDate) : null]
                }
                case 'startDate': {
                    return ['startDate', user.startDate ? new Date(user.startDate) : null]
                }
                case 'status1': {
                    return ['status', user.status1]
                }
                case 'suspensionEndDate': {
                    return ['suspensionEndDate', user.suspensionEndDate ? new Date(user.suspensionEndDate) : null]
                }
                case 'suspensionStartDate': {
                    return ['suspensionStartDate', user.suspensionStartDate ? new Date(user.suspensionStartDate) : null]
                }
                case 'temporaryAbsenceEndDate': {
                    return [
                        'temporaryAbsenceEndDate',
                        user.temporaryAbsenceEndDate ? new Date(user.temporaryAbsenceEndDate) : null
                    ]
                }
                case 'temporaryAbsenceStartDate': {
                    return [
                        'temporaryAbsenceStartDate',
                        user.temporaryAbsenceStartDate ? new Date(user.temporaryAbsenceStartDate) : null
                    ]
                }
                case 'workAreas': {
                    return ['workAreas', user.workAreas ?? []]
                }
                case 'workSuitableUpTo': {
                    return ['workSuitableUpTo', user.workSuitableUpTo ? new Date(user.workSuitableUpTo) : null]
                }
                default: {
                    return [key, value]
                }
            }
        })
    )

    return remappedUser
}

export const absenceReasonToLabel = {
    illness: 'agenda:absence_reason_for_illness',
    personal_reasons: 'agenda:absence_reason_for_personal_reasons',
    training: 'agenda:absence_reason_for_training',
    leave: 'agenda:absence_reason_for_leave'
} as const satisfies Record<ConsultantAbsenceReason, TKey>

export const useUsersSearchParams = () =>
    useParsedSearchParams(
        z.object({
            limit: z.coerce.number().int().positive().default(50),
            page: z.coerce.number().int().positive().default(1),
            search: z.string().trim().optional(),
            filters: z.string().optional(),
            sorts: z.string().optional(),
            view: z.string().optional(),
            columnVisibility: z.string().optional()
        })
    )

export function calculateTrienniumPeriod(inputDate: Date): Period {
    const date = dayjs(inputDate)
    const inputYear = date.year()
    const inputMonth = date.month() + 1

    const BASE_YEAR = 2023
    const refYear = inputMonth >= 5 ? inputYear : inputYear - 1
    const offset = refYear - BASE_YEAR
    const periodIndex = Math.floor(offset / 3)
    const startYear = BASE_YEAR + periodIndex * 3
    const endYear = startYear + 2
    const startDate = dayjs()
        .year(startYear)
        .month(4) // May
        .startOf('month')
        .toDate()

    const endDate = dayjs()
        .year(endYear + 1)
        .month(3) // April
        .endOf('month')
        .toDate()

    return {
        label: `${formatDate(startDate, 'YYYY')}-${formatDate(dayjs(endDate).subtract(1, 'year').toDate(), 'YYYY')}`,
        startDate,
        endDate
    }
}

export function getTrienniaYears(
    currentDate: Date = new Date(),
    {
        baseYear = 2023, // primo anno di riferimento (appartiene a un triennio)
        minYear = 1990, // limite inferiore (incluso)
        fiscalStartMonth = 4 // 0=Gennaio ... 4=Maggio (anno fiscale inizia a Maggio)
    }: {baseYear?: number; minYear?: number; fiscalStartMonth?: number} = {}
): Period[] {
    const d = dayjs(currentDate)

    // Anno fiscale corrente (da fiscalStartMonth in poi vale l'anno in corso, altrimenti l'anno precedente)
    const fiscalYear = d.month() >= fiscalStartMonth ? d.year() : d.year() - 1

    // Allinea al triennio di riferimento relativo a baseYear
    const currentStart = baseYear + Math.floor((fiscalYear - baseYear) / 3) * 3

    const res: Period[] = []
    for (let y = currentStart; y >= minYear; y -= 3) {
        res.push({
            label: `${y}-${y + 2}`,
            startDate: dayjs().year(y).month(4).startOf('month').toDate(),
            endDate: dayjs()
                .year(y + 2)
                .month(3)
                .endOf('month')
                .toDate()
        })
    }
    return res
}
